import React, { useState } from 'react';
import { useDebugRerenders } from '@wix/da-hooks/pkg/useDebugRerenders';
import PopperPortal from '../PopperPortal';
import type {
  PositionableProps,
  PopperPositioningProps,
  ReferenceChildrenProps,
} from '../types';
import { setRefs } from '../../../utils/refHelpers';
import { useBasePopper } from '../hooks/useBasePopper';

export interface Props extends PopperPositioningProps {
  disablePlacementModifier?: boolean;
  className?: string;
  portalClassName?: string;
  usePortal?: boolean;
  children?: React.ReactNode;
  onReferenceClick?: (e: React.MouseEvent) => void;
  onReferenceMouseEnter?: (e: React.MouseEvent) => void;
  onReferenceMouseLeave?: (e: React.MouseEvent) => void;
  renderPopover: (props: PositionableProps) => React.ReactNode;
  /**
   * ONLY USE THIS IF YOU KNOW WHAT YOU ARE DOING
   * By default, children passed to this component will be the reference element for the popper,
   * or alternatively you can pass a renderReference prop. This is helpful if you need to make the
   * reference element be some inner node on the entire structure of the tooltip.
   */
  renderReference?: (props: ReferenceChildrenProps) => React.ReactNode;
}

const PopperPositionedPopover = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      offsetModifier,
      allowFlip,
      disablePlacementModifier,
      className,
      portalClassName,
      placement,
      secondaryPlacement,
      positionFixed,
      eventsEnabled,
      referenceElement: referenceProp,
      usePortal,
      children,
      isVisible = true,
      onReferenceClick,
      onReferenceMouseEnter,
      onReferenceMouseLeave,
      renderPopover,
      renderReference,
    },
    innerRef
  ) => {
    const [referenceElement, setReferenceElement] =
      useState<HTMLElement | null>(null);
    const positioningProps = useBasePopper({
      placement,
      secondaryPlacement,
      eventsEnabled,
      positionFixed,
      offsetModifier,
      allowFlip,
      disablePlacementModifier,
      referenceElement: referenceProp ?? referenceElement,
    });
    useDebugRerenders('PopperPositionedPopover', {
      offsetModifier,
      allowFlip,
      disablePlacementModifier,
      className,
      portalClassName,
      placement,
      secondaryPlacement,
      positionFixed,
      eventsEnabled,
      referenceElement,
      referenceProp,
      usePortal,
      children,
      isVisible,
      onReferenceClick,
      onReferenceMouseEnter,
      onReferenceMouseLeave,
      renderPopover,
      renderReference,
    });

    return (
      <>
        {!referenceProp && renderReference && (
          <div
            className={className}
            ref={innerRef}
            onClick={onReferenceClick}
            onMouseEnter={onReferenceMouseEnter}
            onMouseLeave={onReferenceMouseLeave}
          >
            {renderReference({ ref: setReferenceElement })}
          </div>
        )}
        {!referenceProp && !renderReference && (
          <div
            className={className}
            ref={node => setRefs([setReferenceElement, innerRef], node)}
            onClick={onReferenceClick}
            onMouseEnter={onReferenceMouseEnter}
            onMouseLeave={onReferenceMouseLeave}
          >
            {children}
          </div>
        )}
        {isVisible &&
          (usePortal ? (
            <PopperPortal portalClassName={portalClassName}>
              {renderPopover(positioningProps)}
            </PopperPortal>
          ) : (
            renderPopover(positioningProps)
          ))}
      </>
    );
  }
);
PopperPositionedPopover.displayName = 'PopperPositionedPopover';

export default PopperPositionedPopover;
